import * as Sentry from '@sentry/react'
import {
  enqueueAsyncOperations,
  getAsyncOperationsStatus,
  getFailedAsyncOperations,
  resolveAsyncOperation,
} from 'utils/apis/posAsyncOps'
import {
  appendOpResolution,
  applyReconcile,
  deferSentBatch,
  getAutoRetryableOps,
  getDuePendingOps,
  getSentBatchIds,
  markDiscarded,
  markFailedNow,
  markOpResent,
  markSent,
  pruneDoneOps,
  recordBatchFailure,
  requeueOpsMissingFromServer,
  resetOpToPending,
  setOpError,
  upsertServerFailedOps,
} from './repositories/asyncOps'
import { bumpQueueVersion } from './queueVersion'
import { getQueueDeviceId, getQueueOwner } from './queueContext'
import { isBrowserOnline } from './network'
import { errStatus, errText, isPermanentReject, opErrText, serverMessage } from './opErrors'

/**
 * Uploader antrean transaksi async (Mode Kasir Zero-Loading; port dari
 * kledopos-mobile `services/localdb/asyncUploader.ts`).
 *
 * Hasil flush: `{status:'ok', uploaded}` | `{status:'skipped', reason}` dengan
 * reason 'offline' | 'no-owner' | 'empty' | 'error'.
 */

const LOCK_NAME = 'kledo-pos-async-flush'
const MAX_BATCH = 100
const MAX_BATCHES_PER_RUN = 50

let inFlight = null
let rerunRequested = false

/** batch_id yang di-assign & dikembalikan BE di respons POST. */
const extractBatchId = (res) => res?.data?.data?.batch_id ?? res?.data?.batch_id ?? null

/**
 * op_id (∪ local_id) yang DIKONFIRMASI BE: `accepted` + `duplicated`. null bila BE
 * tak memberi rincian → caller anggap semua terkirim.
 */
const extractAckedIds = (res) => {
  const d = res?.data?.data ?? res?.data ?? {}
  const accepted = Array.isArray(d.accepted) ? d.accepted : null
  const duplicated = Array.isArray(d.duplicated) ? d.duplicated : null
  if (accepted == null && duplicated == null) {
    return null
  }
  return new Set([...(accepted ?? []), ...(duplicated ?? [])].map(String))
}

const toReconcileResults = (res) => {
  const ops = res?.data?.data?.operations ?? res?.data?.operations ?? []
  if (!Array.isArray(ops)) {
    return []
  }
  return ops.map((o) => ({
    op_id: o?.op_id,
    local_id: o?.local_id,
    trans_id: o?.trans_id ?? null,
    status_id: o?.status_id,
    error: o?.error ?? null,
  }))
}

const reconcileBatch = async (batchId) => {
  try {
    const statusRes = await getAsyncOperationsStatus(batchId)
    await applyReconcile(toReconcileResults(statusRes))
  } catch {
    // abaikan; rekonsiliasi ulang setelah jeda.
  }
  await deferSentBatch(batchId)
}

const runFlush = async () => {
  const owner = getQueueOwner()
  if (!owner) {
    return { status: 'skipped', reason: 'no-owner' }
  }
  if (!isBrowserOnline()) {
    return { status: 'skipped', reason: 'offline' }
  }
  const deviceId = getQueueDeviceId()

  // Re-poll batch 'sent' yang jatuh tempo (BE mungkin masih memproses saat
  // rekonsiliasi pertama). Yang belum tuntas ditunda re-poll-nya.
  const sentBatches = await getSentBatchIds(owner)
  // eslint-disable-next-line no-restricted-syntax
  for (const batchId of sentBatches) {
    // eslint-disable-next-line no-await-in-loop
    await reconcileBatch(batchId)
  }
  if (sentBatches.length) {
    bumpQueueVersion()
  }

  let uploaded = 0
  // Ada batch yang gagal → jangan laporkan 'empty' (sukses palsu) di akhir.
  let hadFailure = false
  // BE menolak SELURUH batch bila satu op ditolak permanen → turunkan ke 1 supaya
  // op dikirim satu-satu (urutan tetap) & hanya op bermasalah yang gagal.
  let batchLimit = MAX_BATCH

  // Op FAILED TIDAK memblokir antrean: transaksi kasir independen; op gagal muncul
  // di Riwayat untuk dikirim ulang. Dibatasi 50 batch/run sebagai pengaman loop.
  for (let batch = 0; batch < MAX_BATCHES_PER_RUN; batch += 1) {
    // eslint-disable-next-line no-await-in-loop
    const pending = await getDuePendingOps(batchLimit, owner)
    if (!pending.length) {
      break
    }

    const operations = pending.map((op) => ({
      op_id: op.op_id,
      trans_type_id: op.trans_type_id,
      action: op.action,
      local_id: op.local_id,
      ref_id: op.ref_id ?? null,
      payload: op.payload,
    }))

    let postRes
    try {
      // eslint-disable-next-line no-await-in-loop
      postRes = await enqueueAsyncOperations({
        device_id: deviceId,
        user_id: Number(owner.userId) || 0,
        operations,
      })
    } catch (postErr) {
      if (isPermanentReject(postErr)) {
        if (pending.length > 1) {
          // Belum tahu op mana yang ditolak → kirim ulang satu-satu.
          batchLimit = 1
          // eslint-disable-next-line no-continue
          continue
        }
        // eslint-disable-next-line no-await-in-loop
        await markFailedNow([pending[0].op_id], opErrText(postErr, pending[0]))
        hadFailure = true
        bumpQueueVersion()
        // eslint-disable-next-line no-continue
        continue
      }
      // Umumnya jaringan (mengenai semua) → backoff; setelah batas → FAILED.
      // eslint-disable-next-line no-await-in-loop
      await recordBatchFailure(
        pending.map((op) => op.op_id),
        errText(postErr),
      )
      hadFailure = true
      bumpQueueVersion()
      break
    }

    // batch_id di-assign BE — wajib dipakai untuk cek status (id buatan FE tak dikenal BE).
    const batchId = extractBatchId(postRes)
    if (!batchId) {
      // eslint-disable-next-line no-await-in-loop
      await recordBatchFailure(
        pending.map((op) => op.op_id),
        'Respons server tanpa batch_id',
      )
      hadFailure = true
      bumpQueueVersion()
      break
    }

    // Tandai 'sent' HANYA op yang dikonfirmasi BE; sisanya diperlakukan gagal
    // (backoff → FAILED) supaya tak nyangkut 'sent' selamanya.
    const acked = extractAckedIds(postRes)
    const isAcked = (op) => acked == null || acked.has(op.op_id) || acked.has(op.local_id)
    const sentIds = pending.filter(isAcked).map((op) => op.op_id)
    const unackedIds = pending.filter((op) => !isAcked(op)).map((op) => op.op_id)

    if (sentIds.length) {
      // eslint-disable-next-line no-await-in-loop
      await markSent(sentIds, batchId)
      uploaded += sentIds.length
    }
    if (unackedIds.length) {
      // eslint-disable-next-line no-await-in-loop
      await recordBatchFailure(unackedIds, 'Transaksi tidak dikonfirmasi server')
      hadFailure = true
    }
    if (sentIds.length) {
      // eslint-disable-next-line no-await-in-loop
      await reconcileBatch(batchId)
    }
    bumpQueueVersion()

    if (pending.length < batchLimit) {
      break
    }
  }

  try {
    await pruneDoneOps()
  } catch {
    // non-fatal.
  }

  if (uploaded > 0) {
    return { status: 'ok', uploaded }
  }
  if (hadFailure) {
    return { status: 'skipped', reason: 'error' }
  }
  return { status: 'skipped', reason: 'empty' }
}

/** Jalankan flush dengan Web Lock supaya beberapa tab tak flush bersamaan. */
const runFlushLocked = () => {
  if (typeof navigator !== 'undefined' && navigator.locks?.request) {
    return navigator.locks.request(LOCK_NAME, runFlush)
  }
  return runFlush()
}

/**
 * Upload semua op pending lalu rekonsiliasi. Single-flight per tab + Web Lock
 * antar-tab; enqueue saat upload berjalan memicu satu run ulang setelah selesai.
 * Tidak pernah reject — error tak terduga dilaporkan ke Sentry (aman dipanggil
 * fire-and-forget).
 */
export const flushQueue = () => {
  if (inFlight) {
    rerunRequested = true
    return inFlight
  }
  inFlight = runFlushLocked()
    .catch((err) => {
      Sentry.captureException(err)
      return { status: 'skipped', reason: 'error' }
    })
    .finally(() => {
      inFlight = null
      if (rerunRequested) {
        rerunRequested = false
        flushQueue()
      }
    })
  return inFlight
}

/**
 * Normalisasi daftar `/failed` — paginated `data.data[]`, `operations[]`, atau array
 * langsung. null = bentuk tak dikenali (JANGAN dianggap daftar kosong).
 */
const toServerFailedOps = (res) => {
  const d = res?.data?.data ?? res?.data
  let list = null
  if (Array.isArray(d)) {
    list = d
  } else if (Array.isArray(d?.data)) {
    list = d.data
  } else if (Array.isArray(d?.operations)) {
    list = d.operations
  }
  return list ? list.filter((o) => o?.op_id) : null
}

const FAILED_PER_PAGE = 100
const FAILED_MAX_PAGES = 10

/**
 * Tarik op Failed/Blocked browser ini dari server (berikut riwayat resolusi) & gabungkan
 * ke IndexedDB — server sumber kebenaran; op yang hilang dari lokal (ter-prune / data
 * browser dihapus) ikut muncul lagi. Non-fatal: false bila gagal.
 */
export const syncServerFailedOps = async () => {
  const owner = getQueueOwner()
  if (!owner || !isBrowserOnline()) {
    return false
  }
  try {
    const serverIds = new Set()
    let complete = false
    for (let page = 1; page <= FAILED_MAX_PAGES; page += 1) {
      // eslint-disable-next-line no-await-in-loop
      const res = await getFailedAsyncOperations({
        device_id: getQueueDeviceId(),
        include_history: 1,
        page,
        per_page: FAILED_PER_PAGE,
      })
      const ops = toServerFailedOps(res)
      if (!ops) {
        Sentry.captureMessage('Unrecognized /pos/asyncOperations/failed shape')
        return false
      }
      ops.forEach((o) => serverIds.add(o.op_id))
      // eslint-disable-next-line no-await-in-loop
      await upsertServerFailedOps(ops, owner)
      if (ops.length < FAILED_PER_PAGE) {
        complete = true
        break
      }
    }
    // Hanya bila daftar server terbaca utuh — daftar terpotong akan salah menganggap op
    // di halaman berikutnya sudah selesai.
    if (complete && (await requeueOpsMissingFromServer(serverIds, owner)) > 0) {
      flushQueue()
    }
    bumpQueueVersion()
    return true
  } catch (err) {
    Sentry.captureException(err)
    return false
  }
}

/**
 * Error /resolve yang perlu ditangani khusus oleh UI. `kind`: `stale` = op sudah tak bisa
 * di-resolve (daftar kedaluwarsa, sudah disegarkan); `unsupported` = hanya bisa discard;
 * `rejected` = ditolak permanen (izin/validasi); `network` = tak sampai server.
 */
export class ResolveOpError extends Error {
  constructor(message, kind) {
    super(message)
    this.name = 'ResolveOpError'
    this.kind = kind
  }
}

/**
 * Selesaikan satu op gagal/terblokir (port `resolveOp` mobile). Op yang sudah diterima BE
 * (punya batch_id) → `POST /{opId}/resolve`; sukses = Queued (bukan selesai) → op kembali
 * 'sent' & polling batch dilanjutkan, kecuali `discard` → final. Op yang gagal UPLOAD
 * (tanpa batch_id) hanya bisa dikirim ulang (pending lagi) atau dibatalkan lokal — server
 * belum pernah menerimanya.
 *
 * `keepErrorInline`: penolakan validasi (form buat produk/kontak/pindah shift) TIDAK
 * ditulis ke op — dilempar supaya pesan tampil di form.
 * @returns {Promise<'queued'|'discarded'>}
 */
export const resolveOp = async (
  op,
  action,
  resolutionPayload = {},
  note = undefined,
  keepErrorInline = false,
) => {
  if (!op.batch_id) {
    if (action === 'discard') {
      await markDiscarded(op.op_id)
      bumpQueueVersion()
      return 'discarded'
    }
    if (action !== 'retry_as_is') {
      throw new ResolveOpError('Transaksi belum diterima server — kirim ulang dulu.', 'rejected')
    }
    await resetOpToPending(op.op_id)
    bumpQueueVersion()
    flushQueue()
    return 'queued'
  }
  if (!isBrowserOnline()) {
    throw new ResolveOpError('Tidak dapat terhubung ke server', 'network')
  }

  let res
  try {
    res = await resolveAsyncOperation(op.op_id, {
      action,
      resolution_payload: resolutionPayload,
      ...(note ? { note } : {}),
    })
  } catch (err) {
    const msg = serverMessage(err)
    if (/only failed or blocked/i.test(msg)) {
      // Status lokal kedaluwarsa (mis. sudah diproses ulang di device lain) → poll ulang
      // batch & segarkan daftar gagal dari server.
      await markOpResent(op.op_id)
      await syncServerFailedOps()
      bumpQueueVersion()
      flushQueue()
      throw new ResolveOpError(
        'Status transaksi sudah berubah di server. Daftar diperbarui.',
        'stale',
      )
    }
    if (/no longer supported/i.test(msg)) {
      await setOpError(op.op_id, msg)
      bumpQueueVersion()
      throw new ResolveOpError(
        'Transaksi jenis ini sudah tidak didukung — hanya bisa dibatalkan.',
        'unsupported',
      )
    }
    if (isPermanentReject(err)) {
      const text = opErrText(err, op)
      if (keepErrorInline) {
        // Tampil di bawah field form — pesan validasi BE tanpa prefix status; 403 tetap
        // pakai teks lengkap supaya nama izin yang kurang terlihat.
        throw new ResolveOpError(errStatus(err) === 403 ? text : msg || text, 'rejected')
      }
      await setOpError(op.op_id, text)
      bumpQueueVersion()
      throw new ResolveOpError(text, 'rejected')
    }
    throw new ResolveOpError(errText(err), 'network')
  }

  const data = res?.data?.data ?? res?.data ?? {}
  if (data?.resolution) {
    await appendOpResolution(op.op_id, data.resolution)
  }
  if (action === 'discard' || Number(data?.operation?.status_id) === 6) {
    await markDiscarded(op.op_id)
    bumpQueueVersion()
    return 'discarded'
  }
  await markOpResent(op.op_id)
  bumpQueueVersion()
  flushQueue()
  return 'queued'
}

/**
 * "Sinkronkan Sekarang": jalankan ulang op gagal milik kasir aktif yang AMAN di-retry
 * massal (`internal_error`, `target_not_found`). Tipe lain (terutama duplikat) wajib
 * diputuskan kasir satu per satu. Kembalikan jumlah yang berhasil di-resolve; kegagalan
 * per op dicatat ke op itu & tak menghentikan yang lain.
 */
export const retryServerFailedOps = async () => {
  const owner = getQueueOwner()
  if (!owner || !isBrowserOnline()) {
    return 0
  }
  const ops = await getAutoRetryableOps(owner)
  let resolved = 0
  // eslint-disable-next-line no-restricted-syntax
  for (const op of ops) {
    try {
      // eslint-disable-next-line no-await-in-loop
      await resolveOp(op, 'retry_as_is')
      resolved += 1
    } catch {
      // sudah dicatat ke op oleh resolveOp.
    }
  }
  return resolved
}
